import React from "react";
import { Link } from "react-router-dom";
import "./HomePage.css";
import soccerImage from "../../../assets/images/grayscale_transparent.png";
import SEOHead from "../../common/SEOHead/SEOHead";

function HomePage() {
  // Structured data for services and local business
  const homeStructuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://goalflex.net/#organization",
        name: "GoalFlex",
        url: "https://goalflex.net",
        logo: {
          "@type": "ImageObject",
          url: "https://goalflex.net/logo512.png",
          width: 512,
          height: 512,
        },
        description:
          "Join the ultimate soccer community app. Find pickup games, book quality fields, and connect with players in your area.",
        foundingDate: "2024",
        sameAs: [
          "https://apps.apple.com/us/app/goalflex/id6745612755",
          "https://play.google.com/store/apps/details?id=com.goalflex.app",
        ],
        contactPoint: {
          "@type": "ContactPoint",
          telephone: "+1-555-GOALFLEX",
          contactType: "Customer Service",
        },
        areaServed: {
          "@type": "State",
          name: "Minnesota",
        },
        serviceType: [
          "Soccer Game Booking",
          "Player Matching",
          "Soccer Community Platform",
        ],
      },
      {
        "@type": "LocalBusiness",
        name: "GoalFlex",
        image: "https://goalflex.net/logo512.png",
        description:
          "Premier soccer community platform organizing pickup games, tournaments, and leagues across Minnesota.",
        url: "https://goalflex.net",
        telephone: "+1-555-GOALFLEX",
        address: {
          "@type": "PostalAddress",
          addressLocality: "Minneapolis",
          addressRegion: "MN",
          addressCountry: "US",
        },
        geo: {
          "@type": "GeoCoordinates",
          latitude: 44.9778,
          longitude: -93.265,
        },
        openingHoursSpecification: {
          "@type": "OpeningHoursSpecification",
          dayOfWeek: [
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday",
            "Sunday",
          ],
          opens: "00:00",
          closes: "23:59",
        },
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "4.9",
          reviewCount: "487",
          bestRating: "5",
          worstRating: "1",
        },
        review: [
          {
            "@type": "Review",
            author: {
              "@type": "Person",
              name: "Marcus Rodriguez",
            },
            reviewRating: {
              "@type": "Rating",
              ratingValue: "5",
            },
            reviewBody:
              "Amazing soccer community app! The skill matching is perfect and I've found my regular group here. Much better experience than other sports apps.",
            datePublished: "2024-08-15",
          },
          {
            "@type": "Review",
            author: {
              "@type": "Person",
              name: "Emily Chen",
            },
            reviewRating: {
              "@type": "Rating",
              ratingValue: "5",
            },
            reviewBody:
              "The skill matching is incredible. Every game I join is competitive and fun. The app is smooth and the community is amazing.",
            datePublished: "2024-08-20",
          },
        ],
        priceRange: "Free",
      },
      {
        "@type": "Service",
        name: "Pickup Soccer Games",
        description:
          "GoalFlex organizes pickup soccer games every day in your city. Join competitive and recreational soccer leagues and tournaments.",
        provider: {
          "@id": "https://goalflex.net/#organization",
        },
        areaServed: {
          "@type": "State",
          name: "Minnesota",
        },
        serviceType: "SportsApplication",
        offers: {
          "@type": "Offer",
          price: "0",
          priceCurrency: "USD",
          availability: "https://schema.org/InStock",
        },
      },
      {
        "@type": "Service",
        name: "Soccer Field Booking",
        description:
          "Book premium soccer fields and facilities through our platform. Quality fields with professional maintenance and equipment.",
        provider: {
          "@id": "https://goalflex.net/#organization",
        },
        areaServed: {
          "@type": "State",
          name: "Minnesota",
        },
        serviceType: "SportsApplication",
      },
      {
        "@type": "Service",
        name: "Player Matching & Community",
        description:
          "Connect with local soccer players. Build teams, join leagues, and grow your soccer network.",
        provider: {
          "@id": "https://goalflex.net/#organization",
        },
        areaServed: {
          "@type": "State",
          name: "Minnesota",
        },
        serviceType: "SportsApplication",
      },
      {
        "@type": "WebSite",
        "@id": "https://goalflex.net/#website",
        url: "https://goalflex.net",
        name: "GoalFlex",
        description:
          "Find pickup soccer games, book quality fields, and connect with players in your area.",
        publisher: {
          "@id": "https://goalflex.net/#organization",
        },
        potentialAction: [
          {
            "@type": "SearchAction",
            target: {
              "@type": "EntryPoint",
              urlTemplate: "https://goalflex.net/search?q={search_term_string}",
            },
            "query-input": "required name=search_term_string",
          },
        ],
      },
      {
        "@type": "MobileApplication",
        name: "GoalFlex",
        operatingSystem: ["iOS", "Android"],
        applicationCategory: "SportsApplication",
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "4.8",
          reviewCount: "250",
        },
        offers: {
          "@type": "Offer",
          price: "0",
          priceCurrency: "USD",
        },
        downloadUrl: [
          "https://apps.apple.com/us/app/goalflex/id6745612755",
          "https://play.google.com/store/apps/details?id=com.goalflex.app",
        ],
      },
      {
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: "How do I find pickup soccer games near me?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Download the GoalFlex app and use our location-based search to find pickup soccer games in your area. You can filter by time and game type to find the perfect match.",
            },
          },
          {
            "@type": "Question",
            name: "Is GoalFlex the best soccer app available?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "GoalFlex specializes specifically in soccer with premium field partnerships and a soccer-focused community. We're designed by soccer players, for soccer players.",
            },
          },
          {
            "@type": "Question",
            name: "How much does it cost to join soccer games?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "The GoalFlex app is free to download. Game costs vary by location and field quality, but most pickup games range from $10-25 per person.",
            },
          },
          {
            "@type": "Question",
            name: "What skill levels can play on GoalFlex?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "GoalFlex welcomes all skill levels from beginner to advanced. Games are open to all players looking to enjoy pickup soccer in a fun, inclusive environment.",
            },
          },
        ],
      },
    ],
  };

  return (
    <div className="homepage">
      <SEOHead
        title="GoalFlex - Pickup Soccer Games Near You | Minnesota's #1 Soccer App"
        description="Join 500+ active soccer players on GoalFlex! Find pickup games, book premium fields, and connect with local players. Rated 4.9/5 stars. Download free today!"
        keywords="pickup soccer near me, soccer games Minnesota, soccer app, pickup football, recreational soccer, soccer community, soccer field booking, soccer players, soccer matches, find soccer games"
        url="https://goalflex.net"
        structuredData={homeStructuredData}
      />
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title">Minnesota's #1 Soccer Community App</h1>
            <p className="hero-subtitle">
              Join 500+ active players in Minnesota's premier soccer community.
              Find better games, connect with skilled players, and book premium
              fields. The smarter way to play soccer in Minnesota.
            </p>
            <div className="hero-buttons">
              <Link to="/download" className="btn-primary">
                Download Free App
              </Link>
              <a href="#features" className="btn-secondary">
                Learn More
              </a>
            </div>
            <div className="trust-indicators">
              <div className="trust-item">
                <span className="trust-number">500+</span>
                <span className="trust-label">Active Players</span>
              </div>
              <div className="trust-item">
                <span className="trust-number">4.9★</span>
                <span className="trust-label">App Rating</span>
              </div>
              <div className="trust-item">
                <span className="trust-number">487</span>
                <span className="trust-label">Reviews</span>
              </div>
            </div>
          </div>
          <div className="hero-image">
            <img src={soccerImage} alt="Soccer players in action" />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features-section">
        <div className="container">
          <h2 className="section-title">Why Soccer Players Choose GoalFlex</h2>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">⚡</div>
              <h3>Instant Booking</h3>
              <p>
                Find and join games in under 30 seconds. One-click reservations
                with instant confirmations and smart notifications.
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🎯</div>
              <h3>Smart Matching</h3>
              <p>
                Our AI matches you with games that fit your skill level,
                location preferences, and playing style perfectly.
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🏆</div>
              <h3>Premium Experience</h3>
              <p>
                Verified players, quality fields, professional referees, and
                post-game stats to track your progress.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works">
        <div className="container">
          <h2 className="section-title">Get Playing in 3 Simple Steps</h2>
          <div className="steps-grid">
            <div className="step">
              <div className="step-number">1</div>
              <h3>Browse & Filter</h3>
              <p>
                Search games by location, skill level, time, and game type. Use
                advanced filters to find your perfect match.
              </p>
            </div>
            <div className="step">
              <div className="step-number">2</div>
              <h3>Book Instantly</h3>
              <p>
                Reserve your spot with secure payment. Get instant confirmation,
                field directions, and player details.
              </p>
            </div>
            <div className="step">
              <div className="step-number">3</div>
              <h3>Play & Rate</h3>
              <p>
                Show up and play amazing soccer! Rate players and games to help
                build our trusted community.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section">
        <div className="container">
          <h2 className="section-title">What Players Are Saying</h2>
          <div className="testimonials-grid">
            <div className="testimonial">
              <div className="testimonial-rating">⭐⭐⭐⭐⭐</div>
              <div className="testimonial-content">
                <p>
                  "Amazing soccer community app! The skill matching is perfect
                  and I've found my regular group here. Much better experience
                  than other sports apps."
                </p>
              </div>
              <div className="testimonial-author">
                <strong>Marcus Rodriguez</strong>
                <span>Los Angeles, CA</span>
              </div>
            </div>
            <div className="testimonial">
              <div className="testimonial-rating">⭐⭐⭐⭐⭐</div>
              <div className="testimonial-content">
                <p>
                  "The skill matching is incredible. Every game I join is
                  competitive and fun. The app is smooth and the community is
                  amazing."
                </p>
              </div>
              <div className="testimonial-author">
                <strong>Emily Chen</strong>
                <span>San Francisco, CA</span>
              </div>
            </div>
            <div className="testimonial">
              <div className="testimonial-rating">⭐⭐⭐⭐⭐</div>
              <div className="testimonial-content">
                <p>
                  "As a busy professional, I love how easy it is to find
                  last-minute games. The quality of fields and organization is
                  top-notch."
                </p>
              </div>
              <div className="testimonial-author">
                <strong>David Park</strong>
                <span>Austin, TX</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-grid">
            <div className="stat-item">
              <span className="stat-number">4.9★</span>
              <span className="stat-label">Average Rating</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">487</span>
              <span className="stat-label">Five-Star Reviews</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">500+</span>
              <span className="stat-label">Active Players</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">24/7</span>
              <span className="stat-label">App Available</span>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="container">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <div className="faq-grid">
            <div className="faq-item">
              <h3>How do I find pickup soccer games near me?</h3>
              <p>
                Download the GoalFlex app and use our location-based search to
                find pickup soccer games in your area. You can filter by time
                and game type to find the perfect match.
              </p>
            </div>
            <div className="faq-item">
              <h3>Is GoalFlex the best soccer app available?</h3>
              <p>
                GoalFlex specializes specifically in soccer with premium field
                partnerships and a soccer-focused community. We're designed by
                soccer players, for soccer players.
              </p>
            </div>
            <div className="faq-item">
              <h3>How much does it cost to join soccer games?</h3>
              <p>
                The GoalFlex app is free to download. Game costs vary by
                location and field quality, but most pickup games range from
                $10-25 per person.
              </p>
            </div>
            <div className="faq-item">
              <h3>What skill levels can play on GoalFlex?</h3>
              <p>
                GoalFlex welcomes all skill levels from beginner to advanced.
                Games are open to all players looking to enjoy pickup soccer in
                a fun, inclusive environment.
              </p>
            </div>
            <div className="faq-item">
              <h3>How does GoalFlex compare to other soccer apps?</h3>
              <p>
                Unlike general sports apps, GoalFlex is built specifically for
                soccer. We offer better field quality, more reliable players,
                and features designed to make finding and joining games easy.
              </p>
            </div>
            <div className="faq-item">
              <h3>Is GoalFlex available in Minnesota?</h3>
              <p>
                Yes! GoalFlex is Minnesota's premier soccer community app with
                active games across the Twin Cities and surrounding areas. We're
                expanding to serve more Minnesota communities.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <h2>Ready to Elevate Your Soccer Experience?</h2>
          <p>
            Join the fastest-growing soccer community in America. Your next
            great game is just a click away.
          </p>
          <div className="cta-buttons">
            <Link to="/download" className="btn-primary-large">
              Download App
            </Link>
            <a href="#features" className="btn-secondary-large">
              Learn More
            </a>
          </div>
          <div className="app-badges">
            <span>Available on iOS and Android</span>
          </div>
        </div>
      </section>
    </div>
  );
}

export default HomePage;
